import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import {
  Container, Row, Col, Card, CardBody, Alert,
} from 'reactstrap';

import { Button, ButtonLink } from 'components/Button';
import { completeMfaChallenge } from 'actions/auth';
import {
  startTotpSetup, confirmTotpSetup, isValidCode,
} from 'utils/aws';
import CodeInput from './components/CodeInput';
import QrCanvas from './components/QrCanvas';
import './style.scss';

/**
 * "Set up your authenticator app" — shows the TOTP QR code and manual secret,
 * then verifies a 6-digit code from the user's authenticator app.
 *
 * The QR image and shared secret come from Cognito's setUpTOTP via
 * startTotpSetup() (10s-bounded). Verification goes exclusively through
 * Cognito verifyTOTPSetup via confirmTotpSetup(); MFA setup confirmation is
 * never sent to the backend (Req 10.5).
 */
const SetupAuthenticatorApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // When the user reached this screen mid-sign-in (pool requires TOTP setup),
  // the setup URI + shared secret are passed in route state and the code must
  // be completed via confirmSignIn. Otherwise (authenticated re-setup), we
  // start TOTP setup ourselves and complete via verifyTOTPSetup.
  const viaSignIn = get(location, 'state.viaSignIn', false);
  const stateSetupUri = get(location, 'state.setupUri', '');
  const stateSharedSecret = get(location, 'state.sharedSecret', '');

  const [code, setCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [setupError, setSetupError] = useState('');

  // The otpauth:// URI (encoded into the QR) and the shared secret (shown for
  // manual entry). Seeded from route state on the sign-in-driven path, or from
  // sessionStorage on the authenticated re-setup path so that returning to this
  // screen (e.g. from the help screen) reuses the token already associated with
  // Cognito instead of calling setUpTOTP() again. A second concurrent
  // AssociateSoftwareToken raises ConcurrentModificationException.
  const [setupUri, setSetupUri] = useState(
    stateSetupUri || sessionStorage.getItem('totpSetupUri') || ''
  );
  const [secret, setSecret] = useState(
    stateSharedSecret || sessionStorage.getItem('totpSharedSecret') || ''
  );

  useEffect(() => {
    // Sign-in-driven path relies entirely on the setup details passed in route
    // state; setUpTOTP() cannot run mid-challenge. If we already have that URI,
    // there's nothing to fetch. If we don't (e.g. the user navigated to the
    // help screen and came back, dropping the route state), the challenge
    // context is gone and cannot be rebuilt here — surface a clear error rather
    // than leaving the QR blank.
    if (viaSignIn) {
      if (!setupUri) {
        setSetupError('Your setup session expired. Please sign in again to restart setup.');
      }
      return undefined;
    }

    // Authenticated re-setup path: only call setUpTOTP() once. Re-running it on
    // every remount (e.g. returning from the help screen) can error because a
    // TOTP association is already in progress, which left the QR blank before.
    if (setupUri) return undefined;

    let active = true;
    const loadSetup = async () => {
      try {
        const { setupUri: uri, sharedSecret } = await startTotpSetup();
        if (!active) return;
        // Persist so a remount (Back / help screen) reuses this association
        // rather than triggering a second, concurrent setUpTOTP() call.
        sessionStorage.setItem('totpSetupUri', uri);
        sessionStorage.setItem('totpSharedSecret', sharedSecret);
        setSetupUri(uri);
        setSecret(sharedSecret);
      } catch (err) {
        if (!active) return;
        // Surface the real Cognito error to aid diagnosis; the generic fallback
        // hid the cause (e.g. missing session, TOTP not enabled on the pool).
        console.error('startTotpSetup failed:', err?.name, err?.message, err);
        setSetupError(err?.message || 'Failed to get authenticator setup code. Please sign in again.');
      }
    };
    loadSetup();
    return () => { active = false; };
  }, [viaSignIn, setupUri]);

  const handleVerify = (e) => {
    e.preventDefault();
    // Numeric-only is enforced by CodeInput; require exactly 6 digits before
    // calling Cognito (Req 6.5, 6.8). Invalid input surfaces an input error
    // and makes no Cognito call.
    if (!isValidCode(code)) {
      setHasError(true);
      return;
    }
    // Guard against a double submit: the form's onSubmit and the button's
    // onClick both call this, and a second overlapping updateMFAPreference
    // raises ConcurrentModificationException. Ignore re-entry while a request
    // is already in flight.
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    // Shared success side-effects for both completion paths.
    const onSetupComplete = () => {
      setIsLoading(false);
      // MFA setup confirmation is exclusively through Cognito — no backend
      // confirm-setup call is made (Req 10.5).
      toast.success('Two-factor authentication is now enabled.');
      // Setup is complete: drop the cached association so any future
      // re-setup starts a fresh setUpTOTP() instead of reusing stale details.
      sessionStorage.removeItem('totpSetupUri');
      sessionStorage.removeItem('totpSharedSecret');
      // Allow existing route guards to treat MFA as satisfied for this session.
      sessionStorage.setItem('mfaSatisfied', 'true');
    };

    const onSetupError = (err) => {
      // Cognito rejected the setup code: keep the QR + shared secret shown so
      // the user can re-enter a code (Req 6.7).
      setIsLoading(false);
      setHasError(true);
      toast.error(err?.message || 'That code did not match. Please try again.');
    };

    // Sign-in-driven setup enrolls the authenticator via confirmSignIn, which
    // finishes sign-in. Route completion through the auth saga
    // (completeMfaChallenge) so it persists the Session_Contract and loads the
    // customer/loans data BEFORE navigating — the same routine the code-entry
    // MFA screens use. Completing sign-in here in the component and navigating
    // directly left the dashboard with no session/data and stuck on its loader.
    if (viaSignIn) {
      dispatch(completeMfaChallenge({
        code,
        viaTotpSetup: true,
        navigate,
        success: () => {
          onSetupComplete();
          navigate('/consumer/dashboard');
        },
        fail: onSetupError,
      }));
      return;
    }

    // Authenticated re-setup path: verifyTOTPSetup on an already-signed-in user.
    // A session already exists, so no post-authentication routine is needed.
    confirmTotpSetup(code)
      .then(() => {
        onSetupComplete();
        navigate('/consumer/dashboard');
      })
      .catch(onSetupError);
  };

  return (
    <div className="two-factor-page">
      <Container fluid>
        <Row>
          <Col>
            <Card className="tf-card">
              <CardBody className="p-4">
                <h3 className="tf-title">Set up your authenticator app</h3>

                {setupError && (
                  <Alert color="danger" className="text-center py-2">{setupError}</Alert>
                )}

                <Row className="mt-2 mb-2 align-items-center">
                  <Col xs="auto">
                    <div className="tf-qr">
                      {setupUri
                        ? <QrCanvas value={setupUri} size={148} />
                        : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-center px-1" style={{ color: '#A5B0BB', fontSize: 11 }}>
                            {setupError ? 'Unavailable' : 'Loading QR…'}
                          </div>
                        )}
                    </div>
                  </Col>
                  <Col>
                    <p className="tf-subtitle mb-2">
                      Scan the QR code with your authenticator app, then enter the 6-digit code
                      below.
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p className="tf-info-box mb-0">
                      Can&apos;t scan?
                      Enter this key manually in your app.
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="tf-secret mt-1">{secret}</div>
                  </Col>
                </Row>

                <form onSubmit={handleVerify} className="mt-4">
                  <p className="tf-code-label text-center">Enter 6-digit code</p>
                  <CodeInput
                    value={code}
                    onChange={(val) => { setCode(val); setHasError(false); }}
                    hasError={hasError}
                    disabled={isLoading}
                    autoFocus
                  />

                  <Button
                    type="submit"
                    color="primary"
                    className="w-100 mt-4"
                    isLoading={isLoading}
                    onClick={handleVerify}
                  >
                    Verify &amp; Enable
                  </Button>
                </form>

                <div className="text-center mt-2">
                  <ButtonLink
                    className="tf-link"
                    onClick={() => navigate('/two-factor/authenticator-info')}
                  >
                    New to this? See setup help
                  </ButtonLink>
                </div>

                {!viaSignIn && (
                  <div className="text-center">
                    <ButtonLink
                      className="tf-link"
                      onClick={() => navigate('/two-factor/verify-by-email')}
                    >
                      Not working? Get a code by email instead
                    </ButtonLink>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SetupAuthenticatorApp;
